import React, { useEffect, useState } from 'react';
import { AuditLogResponse } from '@radevents/shared';
import eventService from '../services/eventService';
import { formatDateTime } from '../utils/format';

/**
 * Change history (audit trail) for a single event, shown at the bottom of the
 * event detail page for Admins and Moderators. Reads from GET /events/{id}/history.
 */

const ACTION_LABELS: Record<string, string> = {
  CREATE_EVENT: 'Event erstellt',
  UPDATE_EVENT: 'Event bearbeitet',
  DELETE_EVENT: 'Event gelöscht',
  UPDATE_STATUS: 'Status geändert',
  MERGE_EVENTS: 'Events zusammengeführt',
  DISMISS_DUPLICATE_GROUP: 'Duplikat verworfen',
  PROPOSE_CREATE: 'Neues Event vorgeschlagen',
  PROPOSE_EDIT: 'Bearbeitung vorgeschlagen',
  APPROVE_EDIT: 'Vorschlag genehmigt',
  REJECT_EDIT: 'Vorschlag abgelehnt',
  SYNC_CREATED: 'Import: angelegt',
  SYNC_UPDATED: 'Import: aktualisiert',
  SYNC_ENRICHED: 'Import: angereichert',
  AGENT_ENRICH: 'Agent: angereichert',
  EVENT_REFRESHED_FROM_BREITENSPORT: 'Aktualisiert (breitensport)',
  EVENT_SOURCE_URL_RESOLVED: 'Quell-URL aufgelöst',
  EVENT_SOURCE_DEAD: 'Quelle nicht erreichbar (404)',
  EVENT_SOURCE_UNPARSABLE: 'Quelle nicht lesbar',
  EVENT_DUPLICATE_BLOCKED: 'Update wg. Duplikat blockiert',
  EVENT_DISTANCE_DISCREPANCY: 'Distanz-Abweichung erkannt',
  ENQUEUE_ENRICHMENT: 'Re-Enrich angefragt',
};

const FIELD_LABELS: Record<string, string> = {
  title: 'Titel',
  description: 'Beschreibung',
  locationName: 'Ort/Location',
  street: 'Straße',
  houseNumber: 'Hausnummer',
  postalCode: 'PLZ',
  city: 'Stadt',
  bundesland: '(Bundes)Land',
  eventDate: 'Datum',
  endDate: 'Enddatum',
  startTime: 'Startzeit',
  startTimeEnd: 'Startzeit (Ende)',
  participants: 'Teilnehmer (letzte Auflage)',
  priceTiers: 'Preisstufen',
  organizer: 'Veranstalter',
  organizerId: 'Veranstalter-ID',
  category: 'Kategorie',
  eventType: 'Event-Typ',
  organizerUrl: 'Veranstaltungs-URL',
  flyerUrl: 'Flyer-URL',
  fullyBooked: 'Ausgebucht',
  cancelled: 'Abgesagt',
  tentative: 'Vorläufig',
  registrationStart: 'Anmeldestart',
  registrationMode: 'Anmeldeart',
  registrationState: 'Anmeldestand',
  distances: 'Distanzen',
  links: 'Links',
  status: 'Status',
  externalId: 'Externe ID',
};

const SOURCE_LABELS: Record<string, string> = {
  user_proposal: 'Community-Vorschlag',
  breitensport: 'breitensport.rad-net.de',
  'radsport-events': 'radsport-events.de',
  gctouren: 'GCTouren',
  strava: 'Strava',
  'openclaw-event-enricher': 'OpenClaw — Event Enricher',
  'openclaw-organizer-enricher': 'OpenClaw — Organizer Enricher',
  'openclaw-prorace-enricher': 'OpenClaw — ProRace Enricher',
};

// Keys that are metadata / plumbing, not user-facing field changes.
const META_KEYS = new Set(['fields', 'source', 'editId', 'changedFields', 'title']);

const dot = '#922C57';

const formatValue = (v: unknown): string => {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Ja' : 'Nein';
  if (Array.isArray(v)) return v.length === 0 ? '—' : v.map(formatValue).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  return s.length > 140 ? `${s.slice(0, 140)}…` : s;
};

const fieldLabel = (name: string) => FIELD_LABELS[name] || name;

/** Action headline — special-cases applied community proposals for clarity. */
const actionLabel = (log: AuditLogResponse): string => {
  const src = (log.changes as any)?.source as string | undefined;
  if (log.action === 'UPDATE_EVENT' && src === 'user_proposal') return 'Vorschlag übernommen';
  return ACTION_LABELS[log.action] || log.action;
};

interface FromTo { from?: unknown; to?: unknown }

/** Human-readable actor: username, "System" for sync (user_id 0), or agent name. */
const actorName = (log: AuditLogResponse): string => {
  if (log.userId === 0) return 'System (Import/Sync)';
  if (log.username?.startsWith('openclaw-')) return log.username;
  return log.username || `User #${log.userId}`;
};

const FromToRow: React.FC<{ name: string; from: unknown; to: unknown }> = ({ name, from, to }) => (
  <div style={{ fontSize: 13, lineHeight: 1.45 }}>
    <span style={{ fontWeight: 700, color: '#2C2C2C' }}>{fieldLabel(name)}: </span>
    <span style={{ color: '#9A8A90', textDecoration: 'line-through' }}>{formatValue(from)}</span>
    <span style={{ color: '#6A6A6A', margin: '0 6px' }}>→</span>
    <span style={{ color: '#2C2C2C' }}>{formatValue(to)}</span>
  </div>
);

/** Renders the per-entry detail depending on the action / changes payload shape. */
const EntryDetail: React.FC<{ log: AuditLogResponse }> = ({ log }) => {
  const changes = (log.changes || {}) as Record<string, unknown>;

  // Preferred shape: per-field diff under `fields` (manual edits, applied proposals, sync merges).
  const fields = changes.fields as Record<string, FromTo> | undefined;
  const fieldEntries = fields ? Object.entries(fields) : [];
  if (fieldEntries.length > 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
        {fieldEntries.map(([name, ft]) => (
          <FromToRow key={name} name={name} from={ft?.from} to={ft?.to} />
        ))}
      </div>
    );
  }

  // UPDATE_STATUS: oldStatus → newStatus
  if ('oldStatus' in changes || 'newStatus' in changes) {
    return (
      <div style={{ marginTop: 8 }}>
        <FromToRow name="status" from={changes.oldStatus} to={changes.newStatus} />
      </div>
    );
  }

  // Distance discrepancy between our data and breitensport: { ours, breitensport, reason }.
  if (log.action === 'EVENT_DISTANCE_DISCREPANCY') {
    const km = (arr: unknown) => Array.isArray(arr)
      ? arr.map((d: any) => `${String(d?.km).replace(/\.00$/, '')} km`).join(', ')
      : '—';
    return (
      <div style={{ fontSize: 13, color: '#6A6A6A', marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {typeof changes.reason === 'string' && <div style={{ fontStyle: 'italic' }}>{changes.reason}</div>}
        <div><span style={{ fontWeight: 700, color: '#2C2C2C' }}>Unsere Distanzen:</span> {km(changes.ours)}</div>
        <div><span style={{ fontWeight: 700, color: '#2C2C2C' }}>breitensport:</span> {km(changes.breitensport)}</div>
      </div>
    );
  }

  // Legacy shape: { source, changedFields: ["eventType", ...] } — no from/to captured.
  const changedFields = changes.changedFields;
  if (Array.isArray(changedFields) && changedFields.length > 0) {
    return (
      <div style={{ fontSize: 13, color: '#6A6A6A', marginTop: 8 }}>
        Geänderte Felder: <span style={{ color: '#2C2C2C' }}>{changedFields.map(f => fieldLabel(String(f))).join(', ')}</span>
      </div>
    );
  }

  // Fallback: any remaining non-metadata keys (rare). Metadata like source/editId is hidden.
  const rest = Object.entries(changes).filter(([k, v]) => !META_KEYS.has(k) && v != null && v !== '');
  if (rest.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 8 }}>
      {rest.map(([k, v]) => (
        <span key={k} style={{ fontSize: 13, color: '#6A6A6A' }}>
          <span style={{ fontWeight: 700, color: '#2C2C2C' }}>{fieldLabel(k)}:</span> {formatValue(v)}
        </span>
      ))}
    </div>
  );
};

const EventHistory: React.FC<{ eventId: number }> = ({ eventId }) => {
  const [logs, setLogs] = useState<AuditLogResponse[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    eventService.getEventHistory(eventId)
      .then(data => { if (active) setLogs(data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [eventId]);

  if (error) return null;

  return (
    <section style={{ marginTop: 40, background: '#fff', border: '1px solid #E5E1E1', borderRadius: 16, padding: '24px 28px' }}>
      <div style={{ fontFamily: "'Space Mono',monospace", fontSize: 12, letterSpacing: '0.12em', color: '#6A6A6A', marginBottom: 4 }}>
        ÄNDERUNGSVERLAUF
      </div>
      <div style={{ fontSize: 13, color: '#9A8A90', marginBottom: 20 }}>
        Nur für Admins &amp; Moderatoren sichtbar
      </div>

      {logs === null ? (
        <div style={{ fontSize: 14, color: '#9A8A90' }}>Lädt …</div>
      ) : logs.length === 0 ? (
        <div style={{ fontSize: 14, color: '#9A8A90' }}>Keine Änderungen protokolliert.</div>
      ) : (
        <div style={{ position: 'relative', paddingLeft: 22 }}>
          {/* vertical timeline line */}
          <div style={{ position: 'absolute', left: 5, top: 6, bottom: 6, width: 2, background: '#EDE7E9' }} />
          {logs.map(log => (
            <div key={log.id} style={{ position: 'relative', paddingBottom: 22 }}>
              <div style={{ position: 'absolute', left: -22, top: 4, width: 12, height: 12, borderRadius: '50%', background: dot, border: '2px solid #fff', boxShadow: '0 0 0 2px #EDE7E9' }} />
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 800, fontSize: 15, color: '#2C2C2C' }}>
                  {actionLabel(log)}
                </span>
                <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12, color: '#9A8A90' }}>
                  {formatDateTime(log.createdAt)}
                </span>
              </div>
              <div style={{ fontSize: 13, color: '#6A6A6A', marginTop: 2 }}>
                von <span style={{ fontWeight: 700, color: '#922C57' }}>{actorName(log)}</span>
                {(() => {
                  const src = (log.changes as any)?.source as string | undefined;
                  if (!src) return null;
                  return <span> · Quelle: {SOURCE_LABELS[src] || src}</span>;
                })()}
              </div>
              <EntryDetail log={log} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default EventHistory;
